


import React, { useEffect, useState } from 'react';
import './Rh.css';
import axios from 'axios';

// API específica para o módulo RH
const apiRh = axios.create({
  baseURL: '/api/rh',
});

interface ColaboradorPonto {
  id: number;
  nome: string;
  cpf: string;
  NomeGestor: string;
  turnoHoje: string;
  entradaTurno: string;
  saidaTurno: string;
  ausente: boolean;
  emExpediente: boolean;
  emAlmoco: boolean;
  faltandoMarcacoes: boolean;
  entrada: string;
  almoco: string;
  retornoAlmoco: string;
  saida: string;
  atrasoEntrada?: boolean;
  atrasoSaidaAlmoco?: boolean;
  atrasoRetornoAlmoco?: boolean;
  entradaHorarioDivergente?: boolean;
}

interface IndicadoresPonto {
  ativos: number;
  turnoHoje: number;
  trabalhando: number;
  ausentes: number;
  emAlmoco: number;
  faltandoMarcacoes: number;
}

interface IndicadoresAlmoco {
  emAlmoco: number;
  semIntervaloAlmoco: number;
  erroPeriodoAlmoco: number;
  atrasoSaidaAlmoco: number;
  atrasoEntrada: number;
  atrasoRetornoAlmoco: number;
}


// Função para extrair apenas o horário (HH:mm) de uma string ISO
function formatHora(iso: string) {
  if (!iso) return '-';
  // Aceita formatos ISO, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss', etc.
  // Extrai HH:mm de qualquer posição
  const match = iso.match(/(\d{2}):(\d{2})/);
  if (match) {
    return `${match[1]}:${match[2]}`;
  }
  return '-';
}

// Função para exportar dados para Excel
function exportarParaExcel(dados: any[], nomeArquivo: string, colunas: { key: string; label: string }[]) {
  // Cria o cabeçalho
  const cabecalho = colunas.map(col => col.label).join('\t');
  
  // Cria as linhas
  const linhas = dados.map(item => {
    return colunas.map(col => {
      const valor = item[col.key];
      if (valor === true) return 'Sim';
      if (valor === false) return 'Não';
      if (valor === null || valor === undefined || valor === '') return '-';
      return valor;
    }).join('\t');
  });
  
  // Combina tudo
  const conteudo = [cabecalho, ...linhas].join('\n');
  
  // Cria o blob e faz o download
  const blob = new Blob([conteudo], { type: 'text/plain;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${nomeArquivo}.xls`;
  link.click();
  window.URL.revokeObjectURL(url);
}

const RhModule: React.FC = () => {
    const [ausencias, setAusencias] = useState<{ [id: number]: boolean }>({});
    const [indicadoresExtras, setIndicadoresExtras] = useState<{ justificados: number; faltas: number }>({ justificados: 0, faltas: 0 });
  const [colaboradores, setColaboradores] = useState<ColaboradorPonto[]>([]);
  const [indicadores, setIndicadores] = useState<IndicadoresPonto>({
    ativos: 0,
    turnoHoje: 0,
    trabalhando: 0,
    ausentes: 0,
    emAlmoco: 0,
    faltandoMarcacoes: 0,
  });

  // Removido indicadoresAlmoco não utilizado

  const [data, setData] = useState<string>(() => {
    const hoje = new Date();
    return hoje.toISOString().slice(0, 10);
  });
  const [gestor, setGestor] = useState<string>('');
  const [gestores, setGestores] = useState<string[]>([]);

  useEffect(() => {
        // Buscar ausências justificadas
        apiRh.get('/ausencias', { params: { dataInicio: data } })
          .then(res => {
            if (res.data && res.data.success && Array.isArray(res.data.ausencias)) {
              // Monta um map de id_colaborador com o objeto de ausência completo
              const ausentesMap: { [id: number]: any } = {};
              res.data.ausencias.forEach((a: any) => {
                ausentesMap[a.ID_COLABORADOR] = a;
              });
              setAusencias(ausentesMap);
            } else {
              setAusencias({});
            }
          })
          .catch(() => setAusencias({}));
    // Buscar gestores reais da query de colaboradores
    apiRh.get('/colaboradores')
      .then(res => {
        if (res.data && res.data.success && Array.isArray(res.data.colaboradores)) {
          // Extrair gestores únicos
          const gestoresUnicos: string[] = Array.from(new Set(res.data.colaboradores.map((colab: any) => colab.NomeGestor).filter((g: string | null) => !!g))) as string[];
          setGestores(gestoresUnicos);
        }
      })
      .catch(() => setGestores([]));
  }, []);

  useEffect(() => {
    // Buscar colaboradores do backend
    apiRh.get('/colaboradores')
      .then(res => {
        if (res.data && res.data.success && Array.isArray(res.data.colaboradores)) {
          const colaboradoresBase = res.data.colaboradores.map((colab: any) => ({
            id: colab.id,
            nome: colab.Nome,
            cpf: colab.CPF,
            NomeGestor: colab.NomeGestor || '',
            turnoHoje: '',
            ausente: false,
            emExpediente: false,
            emAlmoco: false,
            faltandoMarcacoes: false,
            entrada: '',
            almoco: '',
            retornoAlmoco: '',
            saida: '',
          }));
          // Buscar turnos e pontos batidos e combinar
          Promise.all([
            apiRh.get('/turnos', { params: { dataInicio: data } }),
            apiRh.get('/pontos', { params: { dataInicio: data } })
          ]).then(([turnosRes, pontosRes]) => {
            // Mapear turnos por colaborador e data
            const turnosMap = new Map();
            if (turnosRes.data && turnosRes.data.success && Array.isArray(turnosRes.data.turnos)) {
              turnosRes.data.turnos.forEach((t: any) => {
                // Extrai apenas a parte da data (YYYY-MM-DD)
                const dataRef = t.DATA_REFERENCIA ? t.DATA_REFERENCIA.slice(0, 10) : '';
                turnosMap.set(`${t.ID_COLABORADOR}_${dataRef}`, t);
              });
            }
            // Mapear pontos batidos por colaborador e data (apenas um registro por colaborador/dia)
            const pontosMap = new Map();
            if (pontosRes.data && pontosRes.data.success && Array.isArray(pontosRes.data.pontos)) {
              pontosRes.data.pontos.forEach((p: any) => {
                const dataRef = p.DATA_REFERENCIA ? p.DATA_REFERENCIA.slice(0, 10) : '';
                const key = `${p.ID_COLABORADOR}_${dataRef}`;
                // Cada colaborador/dia tem apenas um registro de ponto agrupado
                pontosMap.set(key, p);
              });
            }
            // Preencher todos os campos para cada colaborador
            let semIntervaloAlmoco = 0, erroPeriodoAlmoco = 0, atrasoSaidaAlmoco = 0, atrasoEntrada = 0, atrasoRetornoAlmoco = 0;
            
            console.log('[RH DEBUG] Total pontos no mapa:', pontosMap.size);
            console.log('[RH DEBUG] Total colaboradores base:', colaboradoresBase.length);
            
            const colaboradoresFinal = colaboradoresBase.map((colab: ColaboradorPonto) => {
              const ponto = pontosMap.get(`${colab.id}_${data}`);
              const turno = turnosMap.get(`${colab.id}_${data}`);
              
              let entrada = '', almoco = '', retornoAlmoco = '', saida = '';
              if (ponto) {
                entrada = ponto.ENTRADA || '';
                almoco = ponto.ALMOCO || '';
                retornoAlmoco = ponto.RETORNO_ALMOCO || '';
                saida = ponto.SAIDA || '';
              }
              
              // Debug para alguns colaboradores
              if (ponto && entrada) {
                console.log('[RH DEBUG] Colaborador com ponto:', colab.nome, 'entrada:', entrada, 'saida:', saida);
              }
              // Ativo: tem turno hoje
              const temTurno = !!turno;
              
              // Verifica se já passou do horário de entrada do turno
              let jaPassouHorarioEntrada = false;
              if (temTurno && turno.ENTRADA) {
                const agora = new Date();
                const hojeStr = agora.toISOString().slice(0, 10);
                
                // Se a data selecionada é menor que hoje, já passou
                if (data < hojeStr) {
                  jaPassouHorarioEntrada = true;
                } else if (data === hojeStr) {
                  // Se é hoje, verifica se já passou do horário de entrada + margem
                  // Extrai HH:mm do turno.ENTRADA (pode vir como ISO ou HH:mm)
                  const matchEntrada = turno.ENTRADA.match(/(\d{2}):(\d{2})/);
                  if (matchEntrada) {
                    const [, h, m] = matchEntrada;
                    const horarioEntrada = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), Number(h), Number(m) + 10, 0);
                    jaPassouHorarioEntrada = agora > horarioEntrada;
                  }
                }
              }
              
              // Ausente: tem turno, não tem ponto batido E já passou do horário de entrada
              const isAusente = temTurno && !ponto && jaPassouHorarioEntrada;
              // Justificado: ausente e tem registro na tabela de ausencias
              const isJustificado = isAusente && ausencias[colab.id];
              // Em almoço: bateu ponto de almoço, horário de almoço diferente do de entrada, e sem retorno de almoço (mas pode ter saída já)
              const isAlmoco = !!ponto && almoco && almoco !== entrada && !retornoAlmoco;
              // Em expediente: tem entrada batida (trabalhou hoje, independente se já saiu ou não)
              const isExpediente = !!ponto && entrada;
              
              // Debug expediente
              if (isExpediente) {
                console.log('[RH DEBUG] Em expediente:', colab.nome, 'ponto:', !!ponto, 'entrada:', entrada, 'saida:', saida);
              }
              
              // Faltando marcações: para datas passadas, sempre marca como ausente quem está faltando entrada ou saída
              let isFaltandoMarcacoes = false;
              if (temTurno) {
                const faltando = !ponto || !entrada || !saida;
                const hojeStr = new Date().toISOString().slice(0, 10);
                if (faltando) {
                  if (data < hojeStr) {
                    isFaltandoMarcacoes = true;
                  } else {
                    // Se já passou do horário de saída do turno
                    const agora = new Date();
                    const [h, m] = (turno.SAIDA || '').split(':');
                    const dataTurnoSaida = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), Number(h), Number(m), 0);
                    if (agora > dataTurnoSaida) {
                      isFaltandoMarcacoes = true;
                    }
                  }
                }
              }

              // Função para calcular diferença em minutos entre dois horários (HH:mm)
              function diffMinutos(h1: string, h2: string) {
                if (!h1 || !h2) return 0;
                // Extrai apenas HH:mm se vier com data completa
                const extrairHora = (str: string) => {
                  const match = str.match(/(\d{2}):(\d{2})/);
                  return match ? `${match[1]}:${match[2]}` : str;
                };
                const hora1 = extrairHora(h1);
                const hora2 = extrairHora(h2);
                const [h1h, h1m] = hora1.split(':').map(Number);
                const [h2h, h2m] = hora2.split(':').map(Number);
                return (h1h * 60 + h1m) - (h2h * 60 + h2m);
              }

              // Margem de tolerância em minutos
              const margemMinutos = 10;
              // Tempo de intervalo de almoço esperado em minutos (1 hora)
              const intervaloAlmocoMinutos = 60;

              // Atraso Entrada: entrada > turno.ENTRADA + margem
              const atrasoEntrada = temTurno && entrada && turno.ENTRADA && diffMinutos(entrada, turno.ENTRADA) > margemMinutos;
              // Atraso Saída Almoço: almoco > 12:00 + margem (horário padrão de almoço)
              const atrasoSaidaAlmoco = false; // Desabilitado por enquanto
              // Atraso Retorno Almoço: retornoAlmoco > almoco + intervalo esperado + margem
              const atrasoRetornoAlmoco = temTurno && almoco && retornoAlmoco && 
                diffMinutos(retornoAlmoco, almoco) > (intervaloAlmocoMinutos + margemMinutos);
              
              // Entrada Horário Divergente: entrada existe e não está entre turno.ENTRADA - margem e turno.SAIDA + margem
              const entradaHorarioDivergente = temTurno && entrada && turno.ENTRADA && turno.SAIDA && (diffMinutos(entrada, turno.ENTRADA) < -margemMinutos || diffMinutos(entrada, turno.SAIDA) > margemMinutos);

              // Extrai apenas HH:mm dos horários do turno para exibição
              const extrairHora = (str: string) => {
                if (!str) return '';
                const match = str.match(/(\d{2}):(\d{2})/);
                return match ? `${match[1]}:${match[2]}` : str;
              };

              const entradaTurnoFormatada = temTurno && turno.ENTRADA ? extrairHora(turno.ENTRADA) : '';
              const saidaTurnoFormatada = temTurno && turno.SAIDA ? extrairHora(turno.SAIDA) : '';

              return {
                ...colab,
                turnoHoje: temTurno ? `${turno.ENTRADA || ''} - ${turno.SAIDA || ''}` : '',
                entradaTurno: entradaTurnoFormatada,
                saidaTurno: saidaTurnoFormatada,
                ausente: isAusente,
                emExpediente: isExpediente,
                emAlmoco: isAlmoco,
                faltandoMarcacoes: isFaltandoMarcacoes,
                entrada,
                almoco,
                retornoAlmoco,
                saida,
                atrasoEntrada,
                atrasoSaidaAlmoco,
                atrasoRetornoAlmoco,
                entradaHorarioDivergente,
              };
            });
            setColaboradores(colaboradoresFinal);
            // Indicadores extras: justificados e faltas
            const justificados = colaboradoresFinal.filter((c: ColaboradorPonto) => c.ausente && ausencias[c.id]).length;
            const faltas = colaboradoresFinal.filter((c: ColaboradorPonto) => c.ausente && !ausencias[c.id]).length;
            setIndicadoresExtras({ justificados, faltas });
            // Indicadores calculados a partir dos colaboradoresFinal
            setIndicadores({
              ativos: colaboradoresFinal.length,
              turnoHoje: colaboradoresFinal.filter((c: ColaboradorPonto) => c.turnoHoje !== '').length,
              trabalhando: colaboradoresFinal.filter((c: ColaboradorPonto) => c.emExpediente).length,
              ausentes: colaboradoresFinal.filter((c: ColaboradorPonto) => c.ausente).length,
              emAlmoco: colaboradoresFinal.filter((c: ColaboradorPonto) => c.emAlmoco).length,
              faltandoMarcacoes: colaboradoresFinal.filter((c: ColaboradorPonto) => c.faltandoMarcacoes).length,
            });
            // Se precisar dos indicadores de almoço, ajuste aqui. Caso contrário, remova a chamada duplicada.
          }).catch(() => setColaboradores(colaboradoresBase));
        }
      })
      .catch(() => setColaboradores([]));
  }, [data]);

  // Filtra colaboradores pelo gestor selecionado
  const colaboradoresFiltrados = colaboradores.filter(colab => {
    if (!gestor) return true;
    return (colab.NomeGestor || '').trim().toLowerCase() === gestor.trim().toLowerCase();
  });

  // Calcula indicadores com base nos colaboradores filtrados
  const faltasFiltradas = colaboradoresFiltrados.filter(c => c.ausente && !(ausencias && ausencias[c.id])).length;
  const justificadosFiltrados = colaboradoresFiltrados.filter(c => c.ausente && ausencias && ausencias[c.id]).length;
  
  // Debug: verificar quantos ausentes e justificados
  console.log('Total colaboradores filtrados:', colaboradoresFiltrados.length);
  console.log('Ausentes (com c.ausente = true):', colaboradoresFiltrados.filter(c => c.ausente).length);
  console.log('Ausencias map:', ausencias);
  console.log('Justificados:', justificadosFiltrados);
  console.log('Faltas:', faltasFiltradas);
  
  const indicadoresFiltrados = {
    ativos: colaboradoresFiltrados.length,
    turnoHoje: colaboradoresFiltrados.filter((c: ColaboradorPonto) => c.turnoHoje !== '').length,
    trabalhando: colaboradoresFiltrados.filter((c: ColaboradorPonto) => c.emExpediente).length,
    faltas: faltasFiltradas,
    justificados: justificadosFiltrados,
    emAlmoco: colaboradoresFiltrados.filter((c: ColaboradorPonto) => c.emAlmoco).length,
    faltandoMarcacoes: colaboradoresFiltrados.filter((c: ColaboradorPonto) => c.faltandoMarcacoes).length,
  };

  return (
    <div className="rh-module">
      <div className="filtros-rh">
        <label>
          Data:&nbsp;
          <input
            type="date"
            value={data}
            onChange={e => {
              setData(e.target.value);
            }}
          />
        </label>
        <label style={{ marginLeft: 24 }}>
          Gestor:&nbsp;
          <select value={gestor} onChange={e => setGestor(e.target.value)}>
            <option value="">Todos</option>
            {gestores.map(g => (
              <option key={g} value={g}>{g}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="indicadores-cards">
                <div className="card-indicador">
                  <h3>Colaboradores Ativos</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.ativos}</span>
                </div>
                <div className="card-indicador">
                  <h3>Turno Hoje</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.turnoHoje}</span>
                </div>
                <div className="card-indicador">
                  <h3>Faltas</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.faltas}</span>
                </div>
                <div className="card-indicador">
                  <h3>Justificados</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.justificados}</span>
                </div>
                <div className="card-indicador">
                  <h3>Em Expediente</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.trabalhando}</span>
                </div>
                <div className="card-indicador">
                  <h3>Marcações Ausentes</h3>
                  <span className="indicador-valor">{indicadoresFiltrados.faltandoMarcacoes}</span>
                </div>
      </div>

      {/* Card de resumo de problemas igual ao print */}

      {/* Resumo de problemas + tabela de colaboradores com erro, lado a lado */}
      <div style={{ display: 'flex', gap: 24, margin: '24px 0' }}>
        <div className="card-resumo-problemas" style={{ background: '#fff', borderRadius: 12, padding: 16, flex: '0 0 320px', minWidth: 260 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href="#" style={{ color: '#2366d1', textDecoration: 'none', fontSize: 16 }}>Atraso Entrada</a>
              <span style={{ color: '#2366d1', fontWeight: 'bold', fontSize: 16 }}>{colaboradoresFiltrados.filter(c => c.atrasoEntrada).length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href="#" style={{ color: '#2366d1', textDecoration: 'none', fontSize: 16 }}>Atraso Saída Almoço</a>
              <span style={{ color: '#2366d1', fontWeight: 'bold', fontSize: 16 }}>{colaboradoresFiltrados.filter(c => c.atrasoSaidaAlmoco).length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href="#" style={{ color: '#2366d1', textDecoration: 'none', fontSize: 16 }}>Atraso Retorno Almoço</a>
              <span style={{ color: '#2366d1', fontWeight: 'bold', fontSize: 16 }}>{colaboradoresFiltrados.filter(c => c.atrasoRetornoAlmoco).length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href="#" style={{ color: '#2366d1', textDecoration: 'none', fontSize: 16 }}>Entrada Horário Divergente</a>
              <span style={{ color: '#2366d1', fontWeight: 'bold', fontSize: 16 }}>{colaboradoresFiltrados.filter(c => c.entradaHorarioDivergente).length}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a href="#" style={{ color: '#dc2626', textDecoration: 'none', fontSize: 16 }}>Marcação Sem Turno</a>
              <span style={{ color: '#dc2626', fontWeight: 'bold', fontSize: 16 }}>{colaboradoresFiltrados.filter(c => c.turnoHoje === '' && (c.entrada || c.almoco || c.retornoAlmoco || c.saida)).length}</span>
            </div>
          </div>
        </div>
        <div className="tabela-problemas" style={{ background: '#fff', borderRadius: 12, padding: 12, flex: 1, minWidth: 320, maxHeight: 320, overflowY: 'auto', boxShadow: '0 2px 8px #0001' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Colaboradores com Problemas</h3>
            <button 
              onClick={() => {
                const dadosProblemas = colaboradoresFiltrados
                  .filter(colab => {
                    const isAusente = colab.turnoHoje !== '' && !colab.entrada && !colab.almoco && !colab.retornoAlmoco && !colab.saida;
                    const temProblema = colab.atrasoEntrada || colab.atrasoSaidaAlmoco || colab.atrasoRetornoAlmoco || 
                                       colab.entradaHorarioDivergente || colab.faltandoMarcacoes || 
                                       (colab.turnoHoje === '' && (colab.entrada || colab.almoco || colab.retornoAlmoco || colab.saida));
                    return isAusente || temProblema;
                  })
                  .map(colab => {
                    let motivos = [];
                    const isAusente = colab.turnoHoje !== '' && !colab.entrada && !colab.almoco && !colab.retornoAlmoco && !colab.saida;
                    if (isAusente) {
                      const isJustificado = ausencias && ausencias[colab.id];
                      motivos.push(isJustificado ? 'Justificado' : 'Falta');
                    }
                    if (colab.atrasoEntrada) motivos.push('Atraso Entrada');
                    if (colab.atrasoSaidaAlmoco) motivos.push('Atraso Saída Almoço');
                    if (colab.atrasoRetornoAlmoco) motivos.push('Atraso Retorno Almoço');
                    if (colab.entradaHorarioDivergente) motivos.push('Entrada Horário Divergente');
                    if (colab.turnoHoje === '' && (colab.entrada || colab.almoco || colab.retornoAlmoco || colab.saida)) motivos.push('Marcação Sem Turno');
                    if (colab.faltandoMarcacoes) motivos.push('Marcações Ausentes');
                    return { nome: colab.nome, cpf: colab.cpf, motivo: motivos.join(', ') };
                  });
                exportarParaExcel(dadosProblemas, `Problemas_Ponto_${data}`, [
                  { key: 'nome', label: 'Nome' },
                  { key: 'cpf', label: 'CPF' },
                  { key: 'motivo', label: 'Motivo' }
                ]);
              }}
              style={{ 
                padding: '4px 10px', 
                fontSize: 12, 
                cursor: 'pointer', 
                background: '#28a745', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 5,
                fontWeight: 500,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#218838';
                e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#28a745';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
              }}
            >
              <span style={{ fontSize: 13 }}>📊</span> Excel
            </button>
          </div>
          <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Nome</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>CPF</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Motivo</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600 }}>Ausência</th>
              </tr>
            </thead>
            <tbody>
              {colaboradoresFiltrados
                .filter(colab => {
                  // Critério de ausência igual ao da tabela principal
                  const turno = colab.turnoHoje !== '' ? { ENTRADA: colab.entradaTurno, SAIDA: colab.saidaTurno } : null;
                  let jaPassouHorarioEntrada = false;
                  if (turno && turno.ENTRADA) {
                    const agora = new Date();
                    const hojeStr = agora.toISOString().slice(0, 10);
                    if (data < hojeStr) {
                      jaPassouHorarioEntrada = true;
                    } else if (data === hojeStr) {
                      const matchEntrada = turno.ENTRADA.match(/(\d{2}):(\d{2})/);
                      if (matchEntrada) {
                        const [, h, m] = matchEntrada;
                        const horarioEntrada = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), Number(h), Number(m) + 10, 0);
                        jaPassouHorarioEntrada = agora > horarioEntrada;
                      }
                    }
                  }
                  const temTurno = !!turno;
                  const isAusente = temTurno && !colab.entrada && !colab.almoco && !colab.retornoAlmoco && !colab.saida && jaPassouHorarioEntrada;
                  // Tem algum problema de ponto
                  const temProblema = colab.atrasoEntrada ||
                    colab.atrasoSaidaAlmoco ||
                    colab.atrasoRetornoAlmoco ||
                    colab.entradaHorarioDivergente ||
                    colab.faltandoMarcacoes ||
                    (colab.turnoHoje === '' && (colab.entrada || colab.almoco || colab.retornoAlmoco || colab.saida));
                  return isAusente || temProblema;
                })
                .map((colab, idx) => {
                  let motivos = [];
                  // Verifica se é ausente total
                  const isAusente = colab.turnoHoje !== '' && !colab.entrada && !colab.almoco && !colab.retornoAlmoco && !colab.saida;
                  if (isAusente) {
                    const isJustificado = ausencias && ausencias[colab.id];
                    motivos.push(isJustificado ? 'Justificado' : 'Falta');
                  }
                  // Adiciona outros motivos
                  if (colab.atrasoEntrada) motivos.push('Atraso Entrada');
                  if (colab.atrasoSaidaAlmoco) motivos.push('Atraso Saída Almoço');
                  if (colab.atrasoRetornoAlmoco) motivos.push('Atraso Retorno Almoço');
                  if (colab.entradaHorarioDivergente) motivos.push('Entrada Horário Divergente');
                  if (colab.turnoHoje === '' && (colab.entrada || colab.almoco || colab.retornoAlmoco || colab.saida)) motivos.push('Marcação Sem Turno');
                  if (colab.faltandoMarcacoes) motivos.push('Marcações Ausentes');
                  
                  let ausenciaMotivo = '';
                  if (isAusente && ausencias && ausencias[colab.id]) {
                    const justificativa = ausencias[colab.id];
                    if (justificativa && typeof justificativa === 'object') {
                      if (Array.isArray(justificativa)) {
                        ausenciaMotivo = (justificativa[0] as any)?.AUSENCIA || '';
                      } else {
                        ausenciaMotivo = (justificativa as any).AUSENCIA || '';
                      }
                    }
                  }
                  // Apenas ausente sem justificativa tem fundo laranja
                  const temFaltaSemJustificativa = isAusente && !(ausencias && ausencias[colab.id]);
                  return (
                    <tr key={colab.cpf + idx} style={{ borderBottom: '1px solid #f0f0f0', background: temFaltaSemJustificativa ? '#fff7e6' : undefined }}>
                      <td style={{ padding: '4px 8px' }}>{colab.nome}</td>
                      <td style={{ padding: '4px 8px' }}>{colab.cpf}</td>
                      <td style={{ padding: '4px 8px' }}>{motivos.join(', ')}</td>
                      <td style={{ padding: '4px 8px' }}>{ausenciaMotivo}</td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
      <div className="tabela-e-resumo-row" style={{ flexDirection: 'column', gap: '24px' }}>
        <div className="tabela-colaboradores" style={{ overflowX: 'auto', maxWidth: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>RESUMO PONTO</h3>
            <button 
              onClick={() => {
                const dadosExportar = colaboradores
                  .filter(colab => {
                    if (!gestor) return true;
                    return (colab.NomeGestor || '').trim().toLowerCase() === gestor.trim().toLowerCase();
                  })
                  .map(colab => {
                    const isAusente = colab.turnoHoje !== '' && !colab.entrada && !colab.almoco && !colab.retornoAlmoco && !colab.saida;
                    const isJustificado = ausencias && ausencias[colab.id];
                    return {
                      nome: colab.nome,
                      cpf: colab.cpf,
                      entradaTurno: colab.entradaTurno || '-',
                      saidaTurno: colab.saidaTurno || '-',
                      turnoHoje: colab.turnoHoje !== '' ? 'Sim' : 'Não',
                      falta: isAusente ? (isJustificado ? 'Não' : 'Sim') : (colab.ausente ? 'Sim' : 'Não'),
                      emExpediente: colab.emExpediente ? 'Sim' : 'Não',
                      emAlmoco: colab.emAlmoco ? 'Sim' : 'Não',
                      marcacoesAusentes: colab.faltandoMarcacoes ? 'Sim' : 'Não',
                      entrada: formatHora(colab.entrada),
                      almoco: colab.almoco === colab.entrada ? '-' : formatHora(colab.almoco),
                      retornoAlmoco: formatHora(colab.retornoAlmoco),
                      saida: colab.retornoAlmoco && colab.saida && formatHora(colab.retornoAlmoco) === formatHora(colab.saida) ? '-' : formatHora(colab.saida)
                    };
                  });
                exportarParaExcel(dadosExportar, `Resumo_Ponto_${data}`, [
                  { key: 'nome', label: 'Nome' },
                  { key: 'cpf', label: 'CPF' },
                  { key: 'entradaTurno', label: 'Entrada Turno' },
                  { key: 'saidaTurno', label: 'Saída Turno' },
                  { key: 'turnoHoje', label: 'Turno HJ' },
                  { key: 'falta', label: 'Falta' },
                  { key: 'emExpediente', label: 'Em Expediente' },
                  { key: 'emAlmoco', label: 'Em Almoço' },
                  { key: 'marcacoesAusentes', label: 'Marcações Ausentes' },
                  { key: 'entrada', label: 'Entrada' },
                  { key: 'almoco', label: 'Almoço' },
                  { key: 'retornoAlmoco', label: 'Retorno Almoço' },
                  { key: 'saida', label: 'Saída' }
                ]);
              }}
              style={{ 
                padding: '6px 14px', 
                fontSize: 13, 
                cursor: 'pointer', 
                background: '#28a745', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 5,
                fontWeight: 500,
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                transition: 'all 0.2s ease',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#218838';
                e.currentTarget.style.boxShadow = '0 2px 5px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#28a745';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
              }}
            >
              <span style={{ fontSize: 14 }}>📊</span> Excel
            </button>
          </div>
          <table style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th style={{ whiteSpace: 'nowrap', minWidth: 100 }}>Nome</th>
                <th style={{ whiteSpace: 'nowrap', minWidth: 90 }}>CPF</th>
                <th style={{ whiteSpace: 'nowrap', minWidth: 80 }}>Entrada Turno</th>
                <th style={{ whiteSpace: 'nowrap', minWidth: 80 }}>Saída Turno</th>
                <th className="turno" style={{ whiteSpace: 'nowrap', minWidth: 70 }}>Turno HJ</th>
                <th className="ausente" style={{ whiteSpace: 'nowrap', minWidth: 70 }}>Falta</th>
                <th className="expediente" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Em Expediente</th>
                <th className="almoco" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Em Almoço</th>
                <th className="faltando" style={{ whiteSpace: 'nowrap', minWidth: 110 }}>Marcações Ausentes</th>
                <th className="entrada" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Entrada</th>
                <th className="almoco_hora" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Almoço</th>
                <th className="retorno" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Retorno Almoço</th>
                <th className="saida" style={{ whiteSpace: 'nowrap', minWidth: 90 }}>Saída</th>
              </tr>
            </thead>
            <tbody>
              {colaboradores
                .filter(colab => {
                  if (!gestor) return true;
                  // Compara ignorando maiúsculas/minúsculas e espaços
                  return (colab.NomeGestor || '').trim().toLowerCase() === gestor.trim().toLowerCase();
                })
                .map((colab, idx) => {
                  // Usa o flag ausente já calculado no processamento principal
                  // que considera o horário de entrada do turno
                  const isAusente = colab.ausente;
                  const isJustificado = ausencias && ausencias[colab.id];
                  let faltaOuJustificado = 'Não';
                  if (isAusente) {
                    faltaOuJustificado = isJustificado ? 'Justificado' : 'Sim';
                  }
                  // Apenas ausente sem justificativa tem fundo laranja
                  const temFaltaSemJustificativa = isAusente && !isJustificado;
                  return (
                    <tr key={idx} style={temFaltaSemJustificativa ? { background: '#fff7e6' } : undefined}>
                      <td style={temFaltaSemJustificativa ? { fontWeight: 600 } : undefined}>{colab.nome}</td>
                      <td style={temFaltaSemJustificativa ? { fontWeight: 600 } : undefined}>{colab.cpf}</td>
                      <td style={{ textAlign: 'center' }}>{colab.entradaTurno || '-'}</td>
                      <td style={{ textAlign: 'center' }}>{colab.saidaTurno || '-'}</td>
                      <td className="turno">{colab.turnoHoje !== '' ? 'Sim' : 'Não'}</td>
                      <td className="ausente">{faltaOuJustificado}</td>
                      <td className="expediente">{colab.emExpediente ? 'Sim' : 'Não'}</td>
                      <td className="almoco">{colab.emAlmoco ? 'Sim' : 'Não'}</td>
                      <td className="faltando">{colab.faltandoMarcacoes ? 'Sim' : 'Não'}</td>
                      <td className="entrada">{formatHora(colab.entrada)}</td>
                      <td className="almoco_hora">{colab.almoco === colab.entrada ? '-' : formatHora(colab.almoco)}</td>
                      <td className="retorno">{formatHora(colab.retornoAlmoco)}</td>
                      <td className="saida">{colab.retornoAlmoco && colab.saida && formatHora(colab.retornoAlmoco) === formatHora(colab.saida) ? '-' : formatHora(colab.saida)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RhModule;